import {
  type AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  Renderer2,
  signal,
  type OnDestroy,
  NgZone,
} from '@angular/core';
import { BreakpointObserverService } from '@core';

@Directive({
  selector: '[libHideHeaderWithHeight]',
  standalone: true,
})
export class HideHeaderWithHeightDirective implements AfterViewInit, OnDestroy {
  scrollThreshold = input<number>(1);
  omitScreens = input<Array<'mobile' | 'tablet' | 'desktop'>>([]);
  scrollDirectionMode = input<'default' | 'reverse'>('default');

  isHeaderHidden = output<boolean>();

  private el = inject(ElementRef).nativeElement;
  private renderer = inject(Renderer2);
  private breakpointObserverService = inject(BreakpointObserverService);
  private ngZone = inject(NgZone);

  private lastScrollTop = signal(0);
  private hidden = false;
  private initialHeight = '';
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private resizeTimeout?: number;
  private mutationTimeout?: number;

  // Scroll handling properties to prevent conflicts with other scroll listeners
  private minScrollDistance = 1; // Minimum scroll distance to trigger header hide/show
  private scrollDebounceTimeout?: number;
  private isUserScrolling = false;
  private userScrollTimeout?: number;
  private lastScrollTime = 0;
  private scrollToTopTimeout?: number;
  private lastHeaderShowTime = 0;
  private headerShowDebounceTimeout?: number;

  ngAfterViewInit(): void {
    this.initialHeight = this.omitScreens().includes('desktop')
      ? this.el.offsetHeight / 16 + 'rem'
      : this.el.offsetHeight + 'px';
    // Initialize the last scroll position with current scroll
    const initialScroll = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.lastScrollTop.set(initialScroll);

    this.ngZone.runOutsideAngular(() => {
      this.attemptInitialHeightCalculation(0);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.mutationTimeout) {
      clearTimeout(this.mutationTimeout);
    }
    if (this.scrollDebounceTimeout) {
      clearTimeout(this.scrollDebounceTimeout);
    }
    if (this.userScrollTimeout) {
      clearTimeout(this.userScrollTimeout);
    }
    if (this.scrollToTopTimeout) {
      clearTimeout(this.scrollToTopTimeout);
    }
    if (this.headerShowDebounceTimeout) {
      clearTimeout(this.headerShowDebounceTimeout);
    }
  }

  private attemptInitialHeightCalculation(attempt: number): void {
    const maxAttempts = 5;
    const delays = [0, 100, 300, 500, 1000]; // Progressive delays

    setTimeout(() => {
      this.ngZone.run(() => {
        this.calculateAndSetInitialHeight();

        // Set up observers after first calculation
        if (attempt === 0) {
          this.setupObservers();
        }

        // Continue attempting if we haven't reached max attempts
        if (attempt < maxAttempts - 1) {
          this.attemptInitialHeightCalculation(attempt + 1);
        }
      });
    }, delays[attempt] || 1000);
  }

  private calculateAndSetInitialHeight(): void {
    // Temporarily show the element and remove height constraints to get natural height
    const wasHidden = this.hidden;

    // Temporarily disable transition and show element
    this.renderer.setStyle(this.el, 'transition', 'none');
    this.renderer.setStyle(this.el, 'display', 'block');
    this.renderer.setStyle(this.el, 'height', 'auto');
    this.renderer.setStyle(this.el, 'overflow', 'visible');
    this.renderer.setStyle(this.el, 'z-index', '300');

    // Force reflow to get accurate height
    void this.el.offsetHeight;

    // Get the actual height including all content
    const computedHeight = this.el.scrollHeight;
    this.initialHeight = computedHeight + 'px';

    if (wasHidden) {
      this.renderer.setStyle(this.el, 'height', '0');
      this.renderer.setStyle(this.el, 'overflow', 'hidden');
    } else {
      this.renderer.setStyle(this.el, 'height', this.initialHeight);
      this.renderer.setStyle(this.el, 'overflow', 'visible');
    }

    // Restore transition
    this.renderer.setStyle(this.el, 'transition', 'height 0.2s linear, opacity 0.2s linear');
  }

  private setupObservers(): void {
    // Set up ResizeObserver to detect size changes
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.ngZone.run(() => {
          this.debouncedHeightRecalculation('resize');
        });
      });
      this.resizeObserver.observe(this.el);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver((mutations) => {
        let shouldRecalculate = false;

        mutations.forEach((mutation) => {
          // Only recalculate on structural changes (nodes added/removed)
          if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
            // Filter out text nodes and only consider element nodes
            const hasElementChanges =
              Array.from(mutation.addedNodes).some((node) => node.nodeType === Node.ELEMENT_NODE) ||
              Array.from(mutation.removedNodes).some((node) => node.nodeType === Node.ELEMENT_NODE);
            if (hasElementChanges) {
              shouldRecalculate = true;
            }
          }
        });

        if (shouldRecalculate) {
          this.ngZone.run(() => {
            this.debouncedHeightRecalculation('mutation');
          });
        }
      });

      this.mutationObserver.observe(this.el, {
        childList: true,
        subtree: true,
        attributes: false, // Disabled to prevent hover-related recalculations
      });
    }
  }

  private debouncedHeightRecalculation(source: 'resize' | 'mutation'): void {
    // Clear existing timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.mutationTimeout) {
      clearTimeout(this.mutationTimeout);
    }

    const debounceTime = source === 'resize' ? 500 : 300;

    const timeout = setTimeout(() => {
      this.recalculateHeight();
    }, debounceTime);

    if (source === 'resize') {
      this.resizeTimeout = timeout;
    } else {
      this.mutationTimeout = timeout;
    }
  }

  private recalculateHeight(): void {
    const oldHeight = this.initialHeight;

    // Temporarily show the element and remove height constraints to get natural height
    const wasHidden = this.hidden;

    // Temporarily disable transition and show element
    this.renderer.setStyle(this.el, 'transition', 'none');
    this.renderer.setStyle(this.el, 'display', 'block');
    this.renderer.setStyle(this.el, 'height', 'auto');
    this.renderer.setStyle(this.el, 'overflow', 'visible');
    this.renderer.setStyle(this.el, 'z-index', '300');

    // Force reflow to get accurate height
    void this.el.offsetHeight;

    const newHeight = this.el.scrollHeight + 'px';

    // Only update if height actually changed
    if (newHeight !== oldHeight) {
      this.initialHeight = newHeight;
      // If currently visible, animate to the new height
      if (!wasHidden) {
        // Set back to old height first (without transition)
        this.renderer.setStyle(this.el, 'height', oldHeight);

        // Force reflow
        void this.el.offsetHeight;

        // Re-enable transition
        this.renderer.setStyle(this.el, 'transition', 'height 0.3s ease-in-out, opacity 0.2s linear');

        // Force another reflow to ensure transition is applied
        void this.el.offsetHeight;

        // Animate to new height
        this.renderer.setStyle(this.el, 'height', this.initialHeight);
        this.renderer.setStyle(this.el, 'overflow', 'visible');
      } else {
        this.renderer.setStyle(this.el, 'transition', 'height 0.2s linear, opacity 0.2s linear');
        this.renderer.setStyle(this.el, 'height', '0');
        this.renderer.setStyle(this.el, 'overflow', 'hidden');
      }
    } else {
      // No height change, just restore the state
      this.renderer.setStyle(this.el, 'transition', 'height 0.2s linear, opacity 0.2s linear');

      if (wasHidden) {
        this.renderer.setStyle(this.el, 'height', '0');
        this.renderer.setStyle(this.el, 'overflow', 'hidden');
      } else {
        this.renderer.setStyle(this.el, 'height', this.initialHeight);
        this.renderer.setStyle(this.el, 'overflow', 'visible');
      }
    }
  }

  // Window resize listener for additional resize handling
  @HostListener('window:resize')
  onWindowResize(): void {
    this.debouncedHeightRecalculation('resize');
  }

  // Public method to manually recalculate height
  public recalculateHeaderHeight(): void {
    this.recalculateHeight();
  }

  @HostListener('keyup.tab')
  handleTabKeyup(): void {
    this.showHeader();
    this.isHeaderHidden.emit(false);
  }

  @HostListener('window:wheel')
  onWindowWheel(): void {
    this.markUserScrolling();
  }

  @HostListener('window:touchstart')
  onTouchStart(): void {
    this.markUserScrolling();
  }

  @HostListener('window:touchend')
  onTouchEnd(): void {
    this.markUserScrolling();
  }

  private markUserScrolling(): void {
    this.isUserScrolling = true;

    // Clear existing timeout
    if (this.userScrollTimeout) {
      clearTimeout(this.userScrollTimeout);
    }

    // Reset the flag after a delay
    this.userScrollTimeout = window.setTimeout(() => {
      this.isUserScrolling = false;
    }, 150);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const currentScroll = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const previousScroll = this.lastScrollTop();
    const scrollDifference = Math.abs(currentScroll - previousScroll);

    // Check if scrolling should be omitted for certain screen sizes
    const isDesktopAndShouldBeOmitted =
      this.breakpointObserverService.isDesktopOrBigger() && this.omitScreens().includes('desktop');
    const isTabletAndShouldBeOmitted =
      this.breakpointObserverService.isTablet() && this.omitScreens().includes('tablet');
    const isMobileAndShouldBeOmitted =
      this.breakpointObserverService.isMobile() && this.omitScreens().includes('mobile');

    if (isDesktopAndShouldBeOmitted || isTabletAndShouldBeOmitted || isMobileAndShouldBeOmitted) {
      return;
    }

    // Update the last scroll position BEFORE calling handleScrollBehavior
    this.lastScrollTop.set(currentScroll <= 0 ? 0 : currentScroll);

    // Process immediately for responsive header behavior
    this.handleScrollBehavior(currentScroll, previousScroll);
  }

  private handleScrollBehavior(currentScroll: number, previousScroll: number): void {
    // Add top offset to account for header height and make it more responsive
    const topOffset = 150; // Offset to make header hide more responsively
    const adjustedCurrentScroll = currentScroll + topOffset;
    const adjustedPreviousScroll = previousScroll + topOffset;

    const showThreshold = 750; // Show header when within 750px of top
    const hideThreshold = 100; // Hide header when scrolled past 100px from top

    // Detect potential form input scroll-to-top behavior
    const isPotentialFormScroll = this.detectFormScrollBehavior(currentScroll, previousScroll);

    // Skip header changes if this looks like a form input scroll-to-top event
    if (isPotentialFormScroll) {
      return;
    }

    // Show header if very close to top (within 50px) regardless of scroll direction
    if (adjustedCurrentScroll <= 50) {
      this.debouncedShowHeader('very close to top');
    } else if (
      adjustedCurrentScroll <= showThreshold &&
      adjustedCurrentScroll < adjustedPreviousScroll &&
      Math.abs(adjustedCurrentScroll - adjustedPreviousScroll) >= 5
    ) {
      // Near the top AND scrolling up with some movement - show header
      this.debouncedShowHeader('scrolling up near top');
    } else if (
      adjustedCurrentScroll > adjustedPreviousScroll &&
      Math.abs(adjustedCurrentScroll - adjustedPreviousScroll) >= 10
    ) {
      // Scrolling down with at least 10px movement - hide header smoothly
      if (!this.hidden) {
        this.hidden = true;
        this.hideHeader();
        this.isHeaderHidden.emit(true);
      }
    }
  }

  private debouncedShowHeader(reason: string): void {
    if (this.hidden) {
      const now = Date.now();
      const timeSinceLastShow = now - this.lastHeaderShowTime;

      // Clear any existing timeout
      if (this.headerShowDebounceTimeout) {
        clearTimeout(this.headerShowDebounceTimeout);
      }

      // If we just showed the header recently, debounce it to prevent rapid show/hide cycles
      if (timeSinceLastShow < 300) {
        // 300ms debounce
        this.headerShowDebounceTimeout = window.setTimeout(() => {
          this.actuallyShowHeader(reason);
        }, 300 - timeSinceLastShow);
      } else {
        this.actuallyShowHeader(reason);
      }
    }
  }

  private actuallyShowHeader(reason: string): void {
    this.hidden = false;
    this.showHeader();
    this.isHeaderHidden.emit(false);
    this.lastHeaderShowTime = Date.now();
  }

  private detectFormScrollBehavior(currentScroll: number, previousScroll: number): boolean {
    const now = Date.now();
    const timeSinceLastScroll = now - this.lastScrollTime;
    this.lastScrollTime = now;

    // Clear any existing timeout
    if (this.scrollToTopTimeout) {
      clearTimeout(this.scrollToTopTimeout);
    }

    // More comprehensive form scroll detection:
    // 1. Large jump to near top (form focus behavior)
    // 2. Any jump to very top (0-50px) from a significant distance
    // 3. Quick successive scroll events (form behavior pattern)
    const isLargeJumpToTop = previousScroll > 200 && currentScroll < 100;
    const isJumpToVeryTop = previousScroll > 100 && currentScroll < 50;
    const isQuickSuccessiveScroll = timeSinceLastScroll < 50; // Very quick successive scrolls
    const isFormPattern = (isLargeJumpToTop || isJumpToVeryTop) && isQuickSuccessiveScroll;

    // Also detect if we're in a form interaction context
    const activeElement = document.activeElement;
    const isFormElement =
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.getAttribute('role') === 'radio' ||
        activeElement.getAttribute('role') === 'checkbox');

    if (isFormPattern || (isFormElement && currentScroll < 100)) {
      // Set a longer timeout to prevent header changes during form interactions
      this.scrollToTopTimeout = window.setTimeout(() => {}, 1000); // Increased to 1 second

      return true;
    }

    return false;
  }

  private hideHeader(): void {
    // Make sure transition is enabled
    this.renderer.setStyle(this.el, 'transition', 'height 0.3s linear, opacity 0.3s linear');

    // Force reflow to ensure transition works
    void this.el.offsetHeight;

    this.renderer.setStyle(this.el, 'height', '0');
    this.renderer.setStyle(this.el, 'overflow', 'hidden');
  }

  private showHeader(): void {
    this.renderer.setStyle(this.el, 'display', 'block');
    this.renderer.setStyle(this.el, 'z-index', '300');

    // Make sure transition is enabled with consistent timing
    this.renderer.setStyle(this.el, 'transition', 'height 0.3s linear, opacity 0.3s linear');

    // Force reflow to ensure transition works
    void this.el.offsetHeight;

    this.renderer.setStyle(this.el, 'height', this.initialHeight);
  }
}
